import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getPaymentProofUrl } from '../config';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import DjamannLoader from '../components/DjamannLoader';
import { DollarSign, Calendar, BarChart3, Search, Clock, AlertTriangle, ClipboardList, Eye, CheckCircle, User, Mail, Lock, Ban, X } from 'lucide-react';

const AdminPlatformPayments = () => {
  const [projects, setProjects] = useState([]);
  const [overdueProjects, setOverdueProjects] = useState([]);
  const [latePayments, setLatePayments] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [stats, setStats] = useState({ total_recu: 0, total_du: 0, projets_en_retard: 0, projets_actifs: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [confirmingPayment, setConfirmingPayment] = useState(null);
  
  // Recherche et pagination
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filtre de période
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [periodePreset, setPeriodePreset] = useState('all');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateDebut, dateFin]);

  // Reset page quand recherche ou tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeTab]);

  // Gérer les présets de période
  const handlePeriodePreset = (preset) => {
    setPeriodePreset(preset);
    const today = new Date();
    let debut = '';
    let fin = '';
    
    switch (preset) {
      case 'today':
        debut = fin = today.toISOString().split('T')[0];
        break;
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        debut = startOfWeek.toISOString().split('T')[0];
        fin = today.toISOString().split('T')[0];
        break;
      case 'month':
        debut = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        fin = today.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        debut = new Date(today.getFullYear(), quarter * 3, 1).toISOString().split('T')[0];
        fin = today.toISOString().split('T')[0];
        break;
      case 'year':
        debut = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        fin = today.toISOString().split('T')[0];
        break;
      case 'all':
      default:
        debut = '';
        fin = '';
        break;
    }
    
    setDateDebut(debut);
    setDateFin(fin);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Construire les paramètres de requête
      const params = new URLSearchParams();
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [projectsRes, overdueRes, statsRes, pendingRes, lateRes] = await Promise.all([
        api.get('/platform-payments/projects'),
        api.get('/platform-payments/overdue'),
        api.get(`/platform-payments/stats${queryString}`),
        api.get('/platform-payments/pending').catch(() => ({ data: [] })),
        api.get('/platform-payments/late').catch(() => ({ data: [] }))
      ]);
      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
      setOverdueProjects(Array.isArray(overdueRes.data) ? overdueRes.data : []);
      setStats(statsRes.data || { total_recu: 0, total_du: 0, projets_en_retard: 0, projets_actifs: 0 });
      setPendingPayments(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setLatePayments(Array.isArray(lateRes.data) ? lateRes.data : []);
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (projectId) => {
    if (!(await confirm('Suspendre ce projet pour non-paiement ?', { type: 'danger' }))) return;
    try {
      await api.put(`/platform-payments/projects/${projectId}/suspend`);
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suspension');
    }
  };

  const handleReactivate = async (projectId) => {
    if (!(await confirm('Réactiver ce projet ?'))) return;
    try {
      await api.put(`/platform-payments/projects/${projectId}/reactivate`);
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const handleConfirmPayment = async (paymentId) => {
    const methode = prompt('Méthode de paiement utilisée (ex: orange_money, wave, mtn_money):');
    if (!methode) return;
    
    const reference = prompt('Référence de transaction (optionnel):');
    
    setConfirmingPayment(paymentId);
    try {
      await api.put(`/platform-payments/${paymentId}/confirm`, { methode, reference });
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la confirmation du paiement');
    } finally {
      setConfirmingPayment(null);
    }
  };

  if (loading) {
    return <DjamannLoader variant="fullscreen" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 py-10 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-indigo-700"><DollarSign className="w-7 h-7 inline" /> Paiements Plateforme (3%)</h1>
            <p className="text-gray-600 mt-1">Suivi des paiements 3% issus des émissions de partage</p>
          </div>
          <Link
            to="/admin"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition w-fit"
          >
            ← Retour Admin
          </Link>
        </div>

        {/* Filtre de période */}
        <div className="bg-white rounded-xl shadow p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 font-medium"><Calendar className="w-4 h-4 inline" /> Période :</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'Tout' },
                  { value: 'today', label: "Aujourd'hui" },
                  { value: 'week', label: 'Cette semaine' },
                  { value: 'month', label: 'Ce mois' },
                  { value: 'quarter', label: 'Ce trimestre' },
                  { value: 'year', label: 'Cette année' },
                ].map(p => (
                  <button
                    key={p.value}
                    onClick={() => handlePeriodePreset(p.value)}
                    className={`px-3 py-1 rounded-lg text-sm transition ${
                      periodePreset === p.value
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2 lg:ml-auto">
              <span className="text-gray-500 text-sm">Personnalisé :</span>
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => { setDateDebut(e.target.value); setPeriodePreset('custom'); }}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => { setDateFin(e.target.value); setPeriodePreset('custom'); }}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              {(dateDebut || dateFin) && (
                <button
                  onClick={() => handlePeriodePreset('all')}
                  className="px-2 py-1 text-gray-400 hover:text-red-500 transition"
                  title="Réinitialiser"
                >
                  <X className="w-4 h-4 inline" />
                </button>
              )}
            </div>
          </div>
          
          {(dateDebut || dateFin) && (
            <div className="mt-3 pt-3 border-t text-sm text-gray-500">
              <BarChart3 className="w-4 h-4 inline" /> Statistiques pour la période : {dateDebut ? new Date(dateDebut).toLocaleDateString('fr-FR') : 'début'} - {dateFin ? new Date(dateFin).toLocaleDateString('fr-FR') : "aujourd'hui"}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{Number(stats.total_recu || 0).toLocaleString()} XOF</div>
            <div className="text-gray-500 text-xs">Total reçu (confirmé)</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{Number(stats.total_du || 0).toLocaleString()} XOF</div>
            <div className="text-gray-500 text-xs">En attente de paiement</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.nb_a_confirmer || 0}</div>
            <div className="text-gray-500 text-xs">À confirmer (preuve reçue)</div>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.projets_actifs || 0}</div>
            <div className="text-gray-500 text-xs">Projets avec émissions</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow mb-6">
          {/* Barre de recherche */}
          <div className="p-4 border-b">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search className="w-4 h-4" /></span>
              <input
                type="text"
                placeholder="Rechercher par projet, auteur, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>
          
          <div className="flex overflow-x-auto border-b">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-shrink-0 py-4 px-6 font-medium transition ${activeTab === 'pending' ? 'text-orange-600 border-b-2 border-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Clock className="w-4 h-4 inline" /> À confirmer ({pendingPayments.length})
            </button>
            <button
              onClick={() => setActiveTab('waiting')}
              className={`flex-shrink-0 py-4 px-6 font-medium transition ${activeTab === 'waiting' ? 'text-yellow-600 border-b-2 border-yellow-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Clock className="w-4 h-4 inline" /> En attente ({overdueProjects.length})
            </button>
            <button
              onClick={() => setActiveTab('late')}
              className={`flex-shrink-0 py-4 px-6 font-medium transition ${activeTab === 'late' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <AlertTriangle className="w-4 h-4 inline" /> Retards ({latePayments.length})
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-shrink-0 py-4 px-6 font-medium transition ${activeTab === 'all' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <ClipboardList className="w-4 h-4 inline" /> Tous les projets ({projects.length})
            </button>
          </div>
        </div>

        {/* Pending Payments - À confirmer */}
        {activeTab === 'pending' && (() => {
          const filtered = pendingPayments.filter(p => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (p.projet_titre || '').toLowerCase().includes(s) ||
                   (p.auteur_nom || '').toLowerCase().includes(s) ||
                   (p.auteur_email || '').toLowerCase().includes(s);
          });
          const totalPages = Math.ceil(filtered.length / itemsPerPage);
          const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
          
          return (
            <div className="space-y-4">
              {filtered.length > 0 ? (
                <>
                  {search && <p className="text-sm text-gray-500">{filtered.length} résultat(s) pour "{search}"</p>}
                  {paginated.map(payment => (
                    <div key={payment.id} className="bg-white rounded-xl shadow p-6 border-l-4 border-orange-500">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-800">{payment.projet_titre}</h3>
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium"><Clock className="w-3 h-3 inline" /> Preuve envoyée</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            Par : <strong>{payment.auteur_nom}</strong> ({payment.auteur_email})
                          </p>
                          <p className="text-2xl font-bold text-teal-600 mb-2">
                            {Number(payment.montant || 0).toLocaleString()} XOF
                          </p>
                          <p className="text-xs text-gray-500">
                            Soumis le {new Date(payment.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                    
                        <div className="flex flex-col gap-3">
                          {/* Voir la preuve */}
                          {payment.preuve_paiement && (
                            <a
                              href={getPaymentProofUrl(payment.preuve_paiement)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-center font-medium"
                            >
                              <Eye className="w-4 h-4 inline" /> Voir la preuve
                            </a>
                          )}
                      
                          {/* Confirmer */}
                          <button
                            onClick={() => handleConfirmPayment(payment.id)}
                            disabled={confirmingPayment === payment.id}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50"
                          >
                            {confirmingPayment === payment.id ? <><Clock className="w-4 h-4 inline" />...</> : <><CheckCircle className="w-4 h-4 inline" /> Confirmer le paiement</>}
                          </button>
                      
                          {/* Lien vers le projet */}
                          <Link
                            to={`/projects/${payment.project_id}/finances/payments`}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-center text-sm"
                          >
                            Voir le projet →
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        ←
                      </button>
                      <span className="text-sm text-gray-600">Page {currentPage} sur {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                  <div className="text-6xl mb-4"><CheckCircle className="w-16 h-16 mx-auto text-green-500" /></div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Aucun paiement en attente</h2>
                  <p className="text-gray-500">{search ? `Aucun résultat pour "${search}"` : 'Tous les paiements avec preuves ont été confirmés'}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Waiting Payments - En attente (sans preuve) */}
        {activeTab === 'waiting' && (() => {
          const filtered = overdueProjects.filter(p => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (p.projet_titre || '').toLowerCase().includes(s) ||
                   (p.auteur_nom || '').toLowerCase().includes(s) ||
                   (p.auteur_email || '').toLowerCase().includes(s);
          });
          const totalPages = Math.ceil(filtered.length / itemsPerPage);
          const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
          
          return (
            <div className="space-y-4">
              {filtered.length > 0 ? (
                <>
                  {search && <p className="text-sm text-gray-500">{filtered.length} résultat(s) pour "{search}"</p>}
                  {paginated.map(p => (
                    <div key={p.id} className="bg-white rounded-xl shadow p-6 border-l-4 border-yellow-500">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-800">{p.projet_titre}</h3>
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium"><Clock className="w-3 h-3 inline" /> En attente</span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><User className="w-4 h-4 inline" /> <strong>{p.auteur_nom}</strong> ({p.auteur_email})</p>
                            <p className="text-xl font-bold text-teal-600 mt-2">
                              {Number(p.montant || 0).toLocaleString()} XOF
                            </p>
                            <p className="text-xs text-gray-500">
                              Créé le {new Date(p.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/projects/${p.project_id}/finances/payments`}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm"
                          >
                            <BarChart3 className="w-4 h-4 inline" /> Voir le projet
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        ←
                      </button>
                      <span className="text-sm text-gray-600">Page {currentPage} sur {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow p-12 text-center">
                  <span className="text-6xl mb-4 block"><CheckCircle className="w-16 h-16 mx-auto text-green-500" /></span>
                  <h3 className="text-xl font-semibold text-gray-700">Aucun paiement en attente</h3>
                  <p className="text-gray-500 mt-2">{search ? `Aucun résultat pour "${search}"` : 'Tous les paiements plateforme ont une preuve uploadée'}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Late Payments - Retards (+7 jours sans preuve) */}
        {activeTab === 'late' && (() => {
          const filtered = latePayments.filter(p => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (p.projet_titre || '').toLowerCase().includes(s) ||
                   (p.auteur_nom || '').toLowerCase().includes(s) ||
                   (p.auteur_email || '').toLowerCase().includes(s);
          });
          const totalPages = Math.ceil(filtered.length / itemsPerPage);
          const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
          
          return (
            <div className="space-y-4">
              {filtered.length > 0 ? (
                <>
                  {search && <p className="text-sm text-gray-500">{filtered.length} résultat(s) pour "{search}"</p>}
                  {paginated.map(p => (
                    <div key={p.id} className={`bg-white rounded-xl shadow p-6 border-l-4 ${p.payment_blocked_at ? 'border-gray-500' : 'border-red-500'}`}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-gray-800">{p.projet_titre}</h3>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              p.jours_retard > 7 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              <AlertTriangle className="w-4 h-4 inline" /> {p.jours_retard} jour(s) de retard
                            </span>
                            {p.payment_blocked_at && (
                              <span className="px-2 py-1 bg-gray-800 text-white rounded text-xs font-medium">
                                <Lock className="w-4 h-4 inline" /> Projet bloqué
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><User className="w-4 h-4 inline" /> <strong>{p.auteur_nom}</strong> ({p.auteur_email})</p>
                            <div className="flex flex-wrap gap-4 mt-2">
                              <div>
                                <span className="text-gray-500">Bénéfices distribués :</span>
                                <span className="ml-1 font-bold text-teal-600">{Number(p.total_benefices || 0).toLocaleString()} XOF</span>
                              </div>
                              {p.pending_montant && (
                                <div>
                                  <span className="text-gray-500">Paiement en attente :</span>
                                  <span className="ml-1 font-bold text-red-600">{Number(p.pending_montant).toLocaleString()} XOF</span>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Démarrage : {p.date_demarrage ? new Date(p.date_demarrage).toLocaleDateString('fr-FR') : 'Non défini'}
                              {p.last_platform_payment_date && (
                                <> • Dernier paiement : {new Date(p.last_platform_payment_date).toLocaleDateString('fr-FR')}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/projects/${p.project_id}/finances/payments`}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm"
                          >
                            <BarChart3 className="w-4 h-4 inline" /> Voir finances
                          </Link>
                          <a
                            href={`mailto:${p.auteur_email}?subject=Rappel paiement plateforme DJAMANN - ${p.projet_titre}&body=Bonjour ${p.auteur_nom},%0A%0AVotre paiement plateforme (3%%) pour le projet "${p.projet_titre}" est en retard de ${p.jours_retard} jours.%0A%0ABénéfices distribués : ${Number(p.total_benefices || 0).toLocaleString()} XOF%0A%0AMerci de régulariser votre situation pour éviter le blocage de votre projet.%0A%0ACordialement,%0AL'équipe DJAMANN`}
                            className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition text-sm"
                          >
                            <Mail className="w-4 h-4 inline" /> Relancer
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        ←
                      </button>
                      <span className="text-sm text-gray-600">Page {currentPage} sur {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow p-12 text-center">
                  <span className="text-6xl mb-4 block"><CheckCircle className="w-16 h-16 mx-auto text-green-500" /></span>
                  <h3 className="text-xl font-semibold text-gray-700">Aucun projet en retard</h3>
                  <p className="text-gray-500 mt-2">{search ? `Aucun résultat pour "${search}"` : 'Tous les projets sont à jour de leurs paiements plateforme'}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* All Projects */}
        {activeTab === 'all' && (() => {
          const filtered = projects.filter(p => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (p.titre || '').toLowerCase().includes(s) ||
                   (p.author_nom || '').toLowerCase().includes(s);
          });
          const totalPages = Math.ceil(filtered.length / itemsPerPage);
          const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
          
          return (
            <div className="space-y-4">
              {filtered.length > 0 ? (
                <>
                  {search && <p className="text-sm text-gray-500">{filtered.length} résultat(s) pour "{search}"</p>}
                  {paginated.map(p => (
                    <div key={p.id} className="bg-white rounded-xl shadow p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-800">{p.titre}</h3>
                            {p.is_suspended ? (
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium"><Ban className="w-3 h-3 inline" /> Suspendu</span>
                            ) : (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium"><CheckCircle className="w-3 h-3 inline" /> Actif</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><User className="w-4 h-4 inline" /> <strong>{p.author_nom}</strong> ({p.author_email})</p>
                            <p><Calendar className="w-4 h-4 inline" /> Créé le: {p.date_creation ? new Date(p.date_creation).toLocaleDateString('fr-FR') : 'N/A'}</p>
                            {p.nb_emissions > 0 && (
                              <p className="text-teal-600 font-medium"><DollarSign className="w-4 h-4 inline" /> {p.nb_emissions} émission(s) - {Number(p.total_plateforme || 0).toLocaleString()} XOF versé à la plateforme</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/projects/${p.id}/finances/payments`}
                            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm"
                          >
                            <BarChart3 className="w-4 h-4 inline" /> Voir finances
                          </Link>
                          {p.is_suspended ? (
                            <button
                              onClick={() => handleReactivate(p.id)}
                              className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition text-sm"
                            >
                              <CheckCircle className="w-4 h-4 inline" /> Réactiver
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSuspend(p.id)}
                              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition text-sm"
                            >
                              <Ban className="w-4 h-4 inline" /> Suspendre
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        ←
                      </button>
                      <span className="text-sm text-gray-600">Page {currentPage} sur {totalPages}</span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      >
                        →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow p-12 text-center">
                  <span className="text-6xl mb-4 block"><ClipboardList className="w-16 h-16 mx-auto text-gray-400" /></span>
                  <h3 className="text-xl font-semibold text-gray-700">Aucun projet</h3>
                  <p className="text-gray-500 mt-2">{search ? `Aucun résultat pour "${search}"` : 'Les projets apparaîtront ici'}</p>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default AdminPlatformPayments;
